import { minWidthQuery, getObserver } from '@theme/coco-utilities';

if (!customElements.get('smart-marquee')) {
  class SmartMarquee extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.initialized = false;
      this.responsive = this.dataset.marqueeMobile === 'false';
      this.mediaQuery = minWidthQuery(768);
      this.handleResize = this.handleResize.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this._scrollLock = null;
    }

    connectedCallback() {
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);

      if (this.responsive && !this.mediaQuery.matches) return;

      if ('IntersectionObserver' in window) {
        getObserver({ rootMargin: '150px 0px', threshold: 0 }, true, false).observe(this, () => {
          this.initMarquee();
        });
      } else {
        this.initMarquee();
      }

      if (this.responsive) {
        this.mediaQuery.addEventListener('change', this.handleResize);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.clearFocusShift();

      if (this.responsive) {
        this.mediaQuery.removeEventListener('change', this.handleResize);
      }
    }

    handleResize(e) {
      if (e.matches) {
        if (!this.initialized) {
          this.initMarquee();
        }
      } else if (this.initialized) {
        this.destroyMarquee();
      }
    }

    /**
     * Pause scrolling and keep the focused item inside the visible marquee area,
     * even if it has already moved off-screen. Also undo browser horizontal scroll.
     */
    handleFocusIn(event) {
      const target = event.target;
      if (!(target instanceof Element) || !this.contains(target)) return;

      this._scrollLock = { x: window.scrollX, y: window.scrollY };
      this.classList.add('marquee-paused');
      this.revealFocusedItem(target);

      requestAnimationFrame(() => {
        if (!this._scrollLock) return;
        const { x, y } = this._scrollLock;
        if (window.scrollX !== x || window.scrollY !== y) {
          window.scrollTo(x, y);
        }
      });
    }

    handleFocusOut(event) {
      const next = event.relatedTarget;
      if (next instanceof Node && this.contains(next)) return;

      this.clearFocusShift();
    }

    clearFocusShift() {
      this.classList.remove('marquee-paused');
      this.style.removeProperty('--marquee-focus-x');
      this._scrollLock = null;
    }

    revealFocusedItem(target) {
      const item = target.closest('.marquee-item') || target;

      // Measure against the paused animation position (no prior focus offset).
      this.style.setProperty('--marquee-focus-x', '0px');
      void this.offsetWidth;

      const wrapperRect = this.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const pad = 12;
      let delta = 0;

      if (itemRect.width >= wrapperRect.width - pad * 2) {
        delta = wrapperRect.left + pad - itemRect.left;
      } else if (itemRect.left < wrapperRect.left + pad) {
        delta = wrapperRect.left + pad - itemRect.left;
      } else if (itemRect.right > wrapperRect.right - pad) {
        delta = wrapperRect.right - pad - itemRect.right;
      }

      this.style.setProperty('--marquee-focus-x', `${delta}px`);
    }

    initMarquee() {
      if (this.initialized || !this.firstElementChild) return;

      const child = this.firstElementChild;
      const parentWidth = this.offsetWidth;
      const childWidth = child.offsetWidth;
      if (!parentWidth || !childWidth) {
        this._initRetries = (this._initRetries || 0) + 1;
        if (this._initRetries < 30) {
          requestAnimationFrame(() => this.initMarquee());
        }
        return;
      }

      this.initialized = true;
      const requiredClones = Math.ceil(parentWidth / childWidth) + 2;

      const fragment = document.createDocumentFragment();
      for (let i = 0; i < requiredClones; i++) {
        const clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        // clone.setAttribute('inert', '');
        clone.setAttribute('priority-hint', 'low');
        fragment.appendChild(clone);
      }

      this.appendChild(fragment);
      this.classList.add('marquee-initialized');
    }

    destroyMarquee() {
      if (!this.initialized) return;
      this.clearFocusShift();
      while (this.children.length > 1) {
        this.removeChild(this.lastElementChild);
      }
      this.classList.remove('marquee-initialized');
      this.initialized = false;
    }
  }

  customElements.define('smart-marquee', SmartMarquee);
}
